/**
 * WebSocket hook for real-time events from gateway
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const GATEWAY_WS_URL = import.meta.env.VITE_GATEWAY_WS_URL as string | undefined;
// Derive HTTP health URL from WS URL: ws://host/ws → http://host/health
const GATEWAY_HEALTH_URL = GATEWAY_WS_URL
  ? GATEWAY_WS_URL.replace(/^ws/, 'http').replace(/\/ws$/, '/health')
  : undefined;

type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

interface WsMessage {
  type: 'arc_event' | 'arc_events' | 'camera_alert' | 'system' | 'ping';
  data: unknown;
  timestamp: string;
}

interface UseWebSocketOptions {
  topics?: ('arc' | 'cameras' | 'all')[];
  onArcEvent?: (event: unknown) => void;
  onArcEvents?: (events: unknown[]) => void;
  onCameraAlert?: (alert: unknown) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  enabled?: boolean;
  reconnectDelay?: number;
}

export function useGatewayWebSocket(options: UseWebSocketOptions = {}) {
  const {
    topics = ['all'],
    onArcEvent,
    onArcEvents,
    onCameraAlert,
    onConnect,
    onDisconnect,
    enabled = true,
    reconnectDelay = 3000,
  } = options;

  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [lastMessage, setLastMessage] = useState<WsMessage | null>(null);
  const [eventCount, setEventCount] = useState(0);
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const connectingRef = useRef(false);
  const wsErrorRef = useRef(false);

  // Store callbacks in refs to avoid re-renders
  const callbacksRef = useRef({ onArcEvent, onArcEvents, onCameraAlert, onConnect, onDisconnect });
  callbacksRef.current = { onArcEvent, onArcEvents, onCameraAlert, onConnect, onDisconnect };

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    connectingRef.current = false;
    setConnectionState('disconnected');
  }, []);

  const connect = useCallback(async () => {
    // Prevent multiple simultaneous connection attempts
    if (connectingRef.current || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    if (!GATEWAY_WS_URL) {
      console.warn('[WS] Gateway WebSocket URL not configured (VITE_GATEWAY_WS_URL)');
      setConnectionState('error');
      return;
    }

    // Pre-flight HTTP check: avoids browser-native WS error spam when gateway is down
    if (GATEWAY_HEALTH_URL) {
      try {
        const res = await fetch(GATEWAY_HEALTH_URL, {
          signal: AbortSignal.timeout(2000),
          cache: 'no-store',
        });
        if (!res.ok) {
          // Gateway running but not healthy — silent retry
          if (mountedRef.current && enabled) {
            reconnectTimeoutRef.current = setTimeout(() => {
              if (mountedRef.current && enabled) connect();
            }, reconnectDelay);
          }
          return;
        }
      } catch {
        // Gateway not reachable yet — silent retry without attempting WS
        if (mountedRef.current && enabled) {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current && enabled) connect();
          }, reconnectDelay);
        }
        return;
      }
    }

    connectingRef.current = true;
    setConnectionState('connecting');

    try {
      const ws = new WebSocket(GATEWAY_WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        
        connectingRef.current = false;
        setConnectionState('connected');
        callbacksRef.current.onConnect?.();

        // Subscribe to topics
        ws.send(JSON.stringify({ action: 'subscribe', topics }));
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;

        try {
          const msg = JSON.parse(event.data) as WsMessage;
          setLastMessage(msg);

          switch (msg.type) {
            case 'arc_event':
              setEventCount((c) => c + 1);
              callbacksRef.current.onArcEvent?.(msg.data);
              break;

            case 'arc_events':
              if (Array.isArray(msg.data)) {
                setEventCount((c) => c + (msg.data as unknown[]).length);
                callbacksRef.current.onArcEvents?.(msg.data as unknown[]);
              }
              break;

            case 'camera_alert':
              callbacksRef.current.onCameraAlert?.(msg.data);
              break;

            case 'ping':
              // Respond with pong
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ action: 'pong' }));
              }
              break;

            case 'system':
              // Connection acknowledgment, ignore
              break;
          }
        } catch (err) {
          console.error('[WS] Failed to parse message:', err);
        }
      };

      ws.onclose = () => {
        connectingRef.current = false;
        wsRef.current = null;

        if (!mountedRef.current) return;

        setConnectionState('disconnected');
        callbacksRef.current.onDisconnect?.();

        // If there was a WS-level error, use a longer delay to reduce console spam
        const delay = wsErrorRef.current ? Math.max(reconnectDelay, 15_000) : reconnectDelay;
        wsErrorRef.current = false;

        // Schedule reconnect only if still mounted and enabled
        if (mountedRef.current && enabled) {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current && enabled) {
              connect();
            }
          }, delay);
        }
      };

      ws.onerror = () => {
        // Don't log on every error to avoid console spam
        wsErrorRef.current = true;
        connectingRef.current = false;
        if (mountedRef.current) {
          setConnectionState('error');
        }
      };
    } catch (err) {
      console.error('[WS] Failed to connect:', err);
      connectingRef.current = false;
      setConnectionState('error');
    }
  }, [enabled, reconnectDelay, topics]);

  // Connect/disconnect based on enabled prop
  useEffect(() => {
    mountedRef.current = true;

    if (enabled && GATEWAY_WS_URL) {
      connect();
    }

    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, [enabled]); // Only depend on enabled, not connect/disconnect

  // Reconnect when tab becomes visible again.
  // Browsers throttle background timers, so a scheduled reconnect may never
  // fire while the tab is hidden. When the user returns we need an immediate
  // health-check + reconnect if the socket is dead.
  useEffect(() => {
    function handleVisibility() {
      if (document.visibilityState !== 'visible') return;
      if (!enabled || !GATEWAY_WS_URL) return;

      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        // Socket is dead or missing — reconnect immediately
        // Clear any pending reconnect timer first
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        connectingRef.current = false; // Reset guard in case a throttled attempt left it true
        connect();
      }
    }
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [enabled, connect]);

  return {
    connectionState,
    isConnected: connectionState === 'connected',
    lastMessage,
    eventCount,
    connect,
    disconnect,
  };
}
